import { API_ERROR_TYPES, ApiError, isApiError } from './apiError';

const DEFAULT_API_BASE_URL = 'https://simulador-mybeef.sandbox.agro.rocks';

export const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, '');

const getErrorPayload = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  if (data.error && typeof data.error === 'object' && !Array.isArray(data.error)) {
    return { ...data, ...data.error };
  }
  return data;
};

const getErrorType = (status) => {
  if (status === 400 || status === 422) return API_ERROR_TYPES.VALIDATION_ERROR;
  if (status === 401 || status === 403) return API_ERROR_TYPES.UNAUTHORIZED;
  if (status === 404) return API_ERROR_TYPES.NOT_FOUND;
  if (status >= 500) return API_ERROR_TYPES.SERVER_ERROR;
  return API_ERROR_TYPES.HTTP_ERROR;
};

const getDefaultMessage = (type) => {
  switch (type) {
    case API_ERROR_TYPES.VALIDATION_ERROR:
      return 'Verifique os dados informados';
    case API_ERROR_TYPES.UNAUTHORIZED:
      return 'Sua sessão não é válida para esta operação';
    case API_ERROR_TYPES.NOT_FOUND:
      return 'Recurso não encontrado';
    case API_ERROR_TYPES.SERVER_ERROR:
      return 'O servidor não conseguiu concluir a operação';
    default:
      return 'Não foi possível concluir a operação';
  }
};

const parseResponse = async (response, operation) => {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text.trim()) return null;

  try {
    return JSON.parse(text);
  } catch (cause) {
    throw new ApiError({
      type: API_ERROR_TYPES.INVALID_RESPONSE,
      status: response.status,
      code: 'INVALID_JSON_RESPONSE',
      message: 'O servidor retornou uma resposta inválida',
      retryable: response.status >= 500,
      operation,
      cause,
    });
  }
};

const createResponseError = (response, data, operation) => {
  const payload = getErrorPayload(data);
  const type = getErrorType(response.status);

  return new ApiError({
    type,
    status: response.status,
    code: payload.code || `HTTP_${response.status}`,
    message: payload.message || getDefaultMessage(type),
    fields: Array.isArray(payload.fields) ? Object.fromEntries(payload.fields.map(field => [field, payload.message])) : Object.fromEntries(Object.entries(payload.fields && typeof payload.fields === 'object' ? payload.fields : {}).filter(([, value]) => value != null)),
    retryable: response.status === 429 || response.status >= 500,
    operation,
  });
};

const normalizeRequestError = (error, operation) => {
  if (isApiError(error)) return error;

  if (error?.name === 'AbortError') {
    return new ApiError({
      type: API_ERROR_TYPES.ABORTED,
      code: 'REQUEST_ABORTED',
      message: 'A requisição foi cancelada',
      operation,
      cause: error,
    });
  }

  return new ApiError({
    type: API_ERROR_TYPES.NETWORK_ERROR,
    code: 'NETWORK_UNAVAILABLE',
    message: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
    retryable: true,
    operation,
    cause: error,
  });
};

export const request = async (path, {
  method = 'GET',
  body,
  headers = {},
  operation = 'api.request',
  ...fetchOptions
} = {}) => {
  const requestHeaders = {
    Accept: 'application/json',
    ...headers,
  };
  const hasBody = body !== undefined;

  if (hasBody && !requestHeaders['Content-Type']) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      mode: 'cors',
      headers: requestHeaders,
      body: hasBody ? JSON.stringify(body) : undefined,
      ...fetchOptions,
    });
    const data = await parseResponse(response, operation);

    if (!response.ok) {
      throw createResponseError(response, data, operation);
    }

    return data;
  } catch (error) {
    throw normalizeRequestError(error, operation);
  }
};

export const httpClient = {
  get(path, options) {
    return request(path, { ...options, method: 'GET' });
  },
  post(path, body, options) {
    return request(path, { ...options, method: 'POST', body });
  },
};

export default httpClient;
